import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import Select from "react-select";
import { find, isArray, isEmpty } from "lodash";
import visuallyHidden from "theme/visually-hidden";
import FieldError from "components/FieldError";
import formatFieldErrors from "lib/format-field-errors";

const FrontIconWrapper = styled.div`
  align-items: flex-end;
  display: flex;
  position: absolute;
  height: 29px;
  width: 24px;
`;

const OuterContainer = styled.div`
  margin: 0;
  position: relative;
  width: 100%;

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `}
`;

const StyledSelect = styled(Select)`
  margin: 0;
  ${({ theme, highlightColor, leadingIcon }) =>
    theme &&
    css`
      font-weight: 400;
      /* Main Outer Container */

      .gfx-select-container {
        border: none;
        margin-right: 0;
      }

      /* Main Inner Container */

      .gfx-select__control {
        background-color: transparent;
        border-color: ${theme.themeColors.secondary};
        border-left: none;
        border-right: none;
        border-top: none;
        border-radius: 0;
        min-height: 42px;

        /* Input active / focus styles */

        &.gfx-select__control--menu-is-open {
          .gfx-select__indicator {
            svg {
              transform: rotate(180deg);
            }
          }
        }

        &.gfx-select__control--is-focused {
          border-color: ${theme.themeColors.primary};
          box-shadow: none;
        }
      }

      /* Selected Values on left-side container */

      .gfx-select__value-container {
        padding-bottom: 4px;
        padding-left: 0;

        ${leadingIcon &&
        css`
          padding-left: 30px;
        `}
      }

      /* Placeholder styles */

      .gfx-select__placeholder {
        font-size: 1rem;
        line-height: 18px;
        color: ${theme.themeColors.secondary};
        margin-left: 0;
        font-weight: 400;
        /* top: 49%; */
      }

      /* Vertical Line btwn input and down arrow icon */

      .gfx-select__indicator-separator {
        display: none;
      }

      /* Wrapper around down arrow icon */

      .gfx-select__indicator {
        padding-right: 0;
      }

      /* Selected Pill Icons */

      .gfx-select__multi-value {
        border-radius: 21px;
        background-color: ${theme.themeColors.blue1};
        margin-left: 0;
        padding: 1px 4px;
      }

      /* Selected Pill Icon Text Side */

      .gfx-select__multi-value__label {
        color: ${theme.themeColors.darkGreen1};
        font-size: ${theme.fontSize(0)};
      }

      /* Selected Pill Icon Remove "X" Side */

      .gfx-select__multi-value__remove {
        color: ${theme.themeColorShades.secondary500};
        padding-left: 0;

        svg {
          height: 16px;
          width: 16px;
        }

        &:hover {
          background-color: initial;
          border-radius: 0 21px 21px 0;
          color: inherit;
          cursor: pointer;
        }
        transition: ${theme.opacityTransition};
      }

      /* Dropdown Outer Container */

      .gfx-select__menu {
        z-index: 2;
      }

      /* Dropdown Inner Container */

      .gfx-select__menu-list {
      }

      /* Dropdown Select Options */

      .gfx-select__option {
        color: ${theme.themeColorShades.dark800};
        /* Focused Select Option */

        &.gfx-select__option--is-focused {
          background-color: ${highlightColor === "green"
            ? theme.themeColors.lightGreen4
            : theme.themeColors.blue1};
          cursor: pointer;
        }

        &.gfx-select__option--is-selected {
          background-color: ${theme.themeColors.primary};
          color: ${theme.colors.white};
        }
      }
    `}

  /* If error, show red input bttm border */
  ${({ hasInputError, theme }) =>
    hasInputError &&
    css`
      .gfx-select__control {
        border-color: ${theme.themeColors.orange2};
      }
    `}
`;

const SelectLabel = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize(2)};
  color: ${({ theme }) => theme.themeColors.secondary};
  line-height: 18px;
  pointer-events: none;
  position: absolute;
  top: 12px;
  transition: all 0.2s;
  z-index: 1;
  font-weight: 400;

  ${({ showRequiredAsterisk, theme }) =>
    showRequiredAsterisk &&
    css`
      :after {
        content: "*";
        color: ${theme.themeColors.primary};
        font-size: 1rem;
        font-weight: 400;
        line-height: 0.3125rem;
        position: absolute;
        right: -10px;
        top: 50%;
      }
    `}

  ${({ hideLabelForPlaceholder }) =>
    hideLabelForPlaceholder &&
    css`
      ${visuallyHidden};
    `}

  ${({ hasLeadingIcon, isActive }) =>
    hasLeadingIcon &&
    !isActive &&
    css`
      left: 30px;
    `}

  ${({ hasLeadingIcon, isActive }) =>
    hasLeadingIcon &&
    isActive &&
    css`
      left: 30px;
      transform: translate(-30px, -25px);
    `}

  ${({ hasLeadingIcon, isActive }) =>
    !hasLeadingIcon &&
    isActive &&
    css`
      font-size: ${({ theme }) => theme.fontSize(1)};
      transform: translate(0, -25px);
    `}
`;

function SelectDropdownComponent({
  className,
  disabled,
  id,
  isClearable,
  isDisabled,
  isMulti,
  isSearchable,
  label,
  LeadingIcon,
  highlightColor,
  meta: { active, touched, submitError, error: syncError } = {},
  name,
  input: { onChange, value, ...input },
  options,
  placeholder,
  showRequiredAsterisk,
}) {
  const errors = formatFieldErrors({
    submitError,
    syncError,
  });
  const hasError = !!(touched && errors);

  const getValue = useCallback(() => {
    if (!value) {
      return null;
    }
    if (isArray(value) && isMulti) {
      const foundValues = value.map((val) => find(options, { value: val }));
      return foundValues || null;
    }

    // ID's are strings, while references are setup as ints.
    // This is a temp fix until that is resolved in the DB
    return (
      options.find((item) => item?.value?.toString() === value.toString()) ||
      null
    );
  }, [value, options]);

  const [displayValue, setDisplayValue] = useState(getValue());

  useEffect(() => {
    setDisplayValue(getValue());
  }, [value, getValue]);

  const isActive = () => {
    // active if a single value is set for single select. Check for typeof to include booleans
    if (
      !isMulti &&
      (typeof value !== "undefined" ||
        typeof value === "number" ||
        typeof value === "string") &&
      !isEmpty(value)
    ) {
      return true;
    }
    if (!isMulti && typeof value === "boolean") {
      return true;
    }
    return active || value || value?.length;
  };

  const handleChange = (vals) => {
    if (isArray(vals)) {
      const formattedValues = vals.map((val) => val.value);
      onChange(formattedValues);
    } else {
      onChange(vals ? vals.value : null);
    }
  };

  return (
    <OuterContainer className={className} disabled={disabled}>
      <SelectLabel
        htmlFor={id}
        isActive={isActive()}
        hasLeadingIcon={!!LeadingIcon}
        hideLabelForPlaceholder={!!placeholder}
        showRequiredAsterisk={showRequiredAsterisk}
      >
        {label}
      </SelectLabel>
      {LeadingIcon && <FrontIconWrapper>{LeadingIcon}</FrontIconWrapper>}
      <StyledSelect
        {...input}
        aria-label={displayValue}
        className="gfx-select-container"
        classNamePrefix="gfx-select"
        hasInputError={hasError}
        highlightColor={highlightColor}
        inputId={`select-for-${name}-${id}`}
        isClearable={isClearable}
        isMulti={isMulti}
        isDisabled={isDisabled}
        isSearchable={isSearchable}
        leadingIcon={!!LeadingIcon}
        menuPlacement="auto"
        onChange={handleChange}
        placeholder={placeholder}
        options={options}
        value={displayValue}
      />
      <input
        type="hidden"
        invalid={hasError.toString()}
        name={name}
        value={value}
        disabled={disabled}
      />
      <FieldError show={hasError} errors={errors} />
    </OuterContainer>
  );
}

SelectDropdownComponent.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  placeholder: PropTypes.string,
  highlightColor: PropTypes.string,
  isClearable: PropTypes.bool,
  input: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.any,
    checked: PropTypes.bool,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    OnFocus: PropTypes.func,
  }),
  isDisabled: PropTypes.bool,
  isMulti: PropTypes.bool,
  isSearchable: PropTypes.bool,
  label: PropTypes.string,
  LeadingIcon: PropTypes.node,
  meta: PropTypes.shape({
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    submitError: PropTypes.bool,
    success: PropTypes.bool,
  }),
  name: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.any,
    })
  ),
  showRequiredAsterisk: PropTypes.bool,
  value: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.string,
    })
  ),
};
SelectDropdownComponent.defaultProps = {
  className: "",
  disabled: false,
  id: undefined,
  placeholder: null,
  highlightColor: "green",
  input: {
    name: "",
    checked: undefined,
    onBlur: () => {},
    onChange: () => {},
    onFocus: () => {},
    value: [],
  },
  isClearable: false,
  isDisabled: false,
  isMulti: false,
  isSearchable: false,
  label: null,
  LeadingIcon: undefined,
  meta: { error: false, success: false, submitError: false },
  name: "",
  options: [],
  showRequiredAsterisk: false,
  value: [],
};
export default SelectDropdownComponent;
